import { MARKETPLACE_DFW } from '../../../../../constants/index';
import { getMostRecentBooking } from '../../../../../core/util/account';
import {
  formatLocationName,
  getLocationSpecialtyValue,
  isTelemedLocation,
} from '../../../../../core/util/location';
import { isEmptyObject, safeGet } from '../../../../../core/util/object';
import { getLocationAttribute } from '../../../../../core/util/location';
import { TileIterator } from '../../util/tileEnums';
import TileFilter from '../../util/TileFilter';
import { getAgeFromBirthDate } from '../../../../../core/util/date';
import { getRemindersForAge } from '../../Reminders/util/reminders';
import { hoursAfterNow } from '../../../../../core/util/time';
import { PAST_VISIT_DISPLAY_TIME_WINDOW } from '../../../../../config/index';
import { isDirectTelemedBooking } from '../../../../../core/util/booking';
import { sortFn } from '../../Insurance/util/descriptions/insights';
import { isEmptyArray } from '../../../../../core/util/array';

const isInMarketplaceProfile = (userProfile: any) =>
  userProfile && userProfile.market === MARKETPLACE_DFW;

const getRecentLocation = (userProfile: any, userLocations: any, accountSummary: any) => {
  if (isEmptyObject(userLocations)) {
    return null;
  }

  const recentBooking = getMostRecentBooking(userProfile) || getMostRecentBooking(accountSummary);
  if (!recentBooking || !recentBooking.location_id) {
    return null;
  }

  return userLocations[recentBooking.location_id];
};

const getPediatricLocation = (userProfile: any, userLocations: any, accountSummary: any) => {
  const location = getRecentLocation(userProfile, userLocations, accountSummary);
  if (getLocationAttribute(location, 'isPediatricPatientsAccepted')) {
    return location;
  }

  return null;
};

const getTelemedLocation = (userLocations: any) => {
  for (const location of Object.values(userLocations)) {
    if (isTelemedLocation(location)) {
      return location;
    }
  }

  return null;
};

const buildTileLocationsProp = (
  userProfile: any,
  userLocations: any,
  traditionalLocations: any,
  favoriteLocations: any,
  accountSummary: any,
  nearbyLocations: any
) => ({
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 2.
  pediatric: getPediatricLocation(userProfile, userLocations),
  recent: getRecentLocation(userProfile, userLocations, accountSummary),
  telemed: getTelemedLocation(userLocations),
  traditional: traditionalLocations,
  favorite: favoriteLocations,
  user: userLocations,
  nearby: nearbyLocations,
});

const getBookingTiles = (tiles: any, upcomingBookings: any, pastBookings: any, locations: any) => {
  let permuted = [];
  for (let booking of upcomingBookings.filter((b: any) => !isDirectTelemedBooking(b))) {
    for (let tile of tiles) {
      permuted.push({ ...tile, booking });
    }
  }
  for (let booking of pastBookings.filter(
    (b: any) =>
      hoursAfterNow(b.appointment_date) > -1 * PAST_VISIT_DISPLAY_TIME_WINDOW &&
      !isDirectTelemedBooking(b)
  )) {
    for (let tile of tiles) {
      permuted.push({ ...tile, booking });
    }
  }
  permuted = permuted.filter((tile) => {
    if (!locations) {
      return false;
    }

    return locations.user && locations.user[tile.booking.location_id];
  });

  return permuted;
};

const getProviderTypeTiles = (tile: any, locations: any) => {
  const decoratedTiles = {};

  for (const locationId in locations.user) {
    if (!locations.user.hasOwnProperty(locationId)) continue;

    const location = locations.user[locationId];
    const providerType = getLocationSpecialtyValue(location);
    if (!providerType || decoratedTiles.hasOwnProperty(providerType)) continue;

    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    decoratedTiles[providerType] = {
      ...tile,
      providerType,
      location,
    };
  }

  return Object.values(decoratedTiles);
};

const getIterableList = (
  userProfile: any,
  category: any,
  index: any,
  locations: any,
  upcomingBookings: any,
  pastBookings: any,
  accountSummary: any,
  insuranceDetails: any
) => {
  const shouldShowTile = (tile: any) =>
    TileFilter.filter(tile, userProfile, locations, accountSummary, insuranceDetails);

  const decoratedTiles = category.tiles.map((tile: any) => ({
    ...tile,
    categoryTitle: category.title,
    categoryId: category.id,
  }));

  let tiles = [];
  switch (category.iterator) {
    case TileIterator.VISITS:
      tiles = getBookingTiles(decoratedTiles, upcomingBookings, pastBookings, locations);
      break;
    case TileIterator.TILES:
      tiles = decoratedTiles;
      break;
    case TileIterator.TRADITIONAL_LOCATIONS: {
      if (!decoratedTiles.length) {
        break;
      }

      const tile = decoratedTiles[0];
      tiles = locations.traditional.map((location: any) => ({
        ...tile,
        location,
        title: formatLocationName(location),
      }));
      break;
    }

    case TileIterator.NEARBY_LOCATIONS: {
      if (!decoratedTiles.length) {
        break;
      }

      const tile = decoratedTiles[0];
      tiles = locations.nearby.map((location: any) => ({
        ...tile,
        location,
        title: formatLocationName(location),
      }));
      break;
    }

    case TileIterator.HEALTH_CHECKLIST: {
      const reminderIds = getRemindersForAge(getAgeFromBirthDate(userProfile.birth_date));
      tiles = decoratedTiles.filter((tile: any) => reminderIds.includes(tile.id));
      break;
    }

    case TileIterator.FAVORITES: {
      tiles = getProviderTypeTiles(decoratedTiles[0], locations);
      break;
    }

    case TileIterator.SAVED: {
      if (!locations.favorite.length || !decoratedTiles.length) break;
      tiles = locations.favorite.map((location: any) => ({
        ...decoratedTiles[0],
        providerType: getLocationSpecialtyValue(location),
        location,
        title: formatLocationName(location),
      }));
      break;
    }

    default:
      break;
  }

  const filtered = tiles.filter(shouldShowTile);
  if (filtered.some((t: any) => t.hasOwnProperty('sortOrder'))) {
    let insuranceProfile;
    if (
      accountSummary &&
      !isEmptyArray(accountSummary.insurance_profiles) &&
      userProfile.insurance_profile_id
    ) {
      insuranceProfile = accountSummary.insurance_profiles.find(
        (p: any) => p.insurance_profile_id === userProfile.insurance_profile_id
      );
    }
    const insurerCode = safeGet(insuranceProfile)('insurer_code');
    filtered.sort(sortFn(insurerCode));
  }
  return filtered;
};

const injectProps = (field: any, userProfile: any, locations: any) => {
  if (typeof field === 'function') return field({ userProfile, locations });
  return field;
};

export {
  PAST_VISIT_DISPLAY_TIME_WINDOW,
  getRecentLocation,
  getPediatricLocation,
  getTelemedLocation,
  buildTileLocationsProp,
  getIterableList,
  isInMarketplaceProfile,
  injectProps,
  getProviderTypeTiles,
};
